import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import AuthService from 'services/auth'
import cutileiApi, { UPLOAD_TIMEOUT } from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import viaCepApi from 'services/viaCepApi'
import AlertDialog from 'components/AlertDialog'
import MainButton from 'components/Buttons/MainButton'
import submitOnEnter from 'utils/submitOnEnter'
import addressValidator from 'validators/addressValidator'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { MdNavigateNext } from 'react-icons/md'
import { DateTime } from 'luxon'
import { Formik } from 'formik'

import {
  Form,
  SubTitle,
  AddressContainer,
  LoadingBackdrop,
  FormField,
  WarningText,
  Input,
  MaskedInput,
  Row,
  ErrorContainer
} from '../styles'

export default function AddressForm ({
  professionalData = {},
  onFormSubmitted: handleFormSubmitted = () => null
}) {
  const [loadingAddress, setLoadingAddress] = useState (false)
  const [createdProfessional, setCreatedProfessional] = useState (null)
  const [showOkDialog, setShowOkDialog] = useState (false)
  const [errorMessage, setErrorMessage] = useState (null)
  const businessId = AuthService.getBusinessId ()
  const token = AuthService.getToken ()
  const navigate = useNavigate ()
  const location = useLocation ()
  const isTutorial = !!location.state?.isTutorial

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  const handleProfessionalAdded = () => {
    navigate (
      isTutorial ? '/business' : `/business/professionals/${createdProfessional?.id}/edit`,
      { replace: true }
    )
  }

  const toggleOkDialog = () => setShowOkDialog (!showOkDialog)

  return (
    <Formik
      validationSchema={addressValidator}
      initialValues={professionalData.address}
      onSubmit={async (values, { setSubmitting }) => {
        if (professionalData.access_level.value.codename === 'service') {
          handleFormSubmitted ({ address: values })
        } else try {
          setErrorMessage (null)
          setSubmitting (true)

          const { birth, gender, profile_picture, access_level } = professionalData

          const { data: professional } = await cutileiApi.post (
            `/businesses/${businessId}/professionals`, {
              ...professionalData,
              birth: DateTime.fromFormat (birth, 'ddMMyyyy').toISODate (),
              gender: gender.value,
              access_level_id: access_level.value.id,
              address: values
            }, requestConfig
          )

          if (profile_picture) {
            const formData = new FormData ()
            formData.append ('profile_picture', profile_picture.file, profile_picture.file.name)

            await cutileiApi.put (`/professionals/${professional.id}/profile_picture`, formData, {
              // Upload/download nao cabe no timeout padrao: rede movel ruim leva mais que 30 s.
              timeout: UPLOAD_TIMEOUT,
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            })
          }

          setSubmitting (false)
          setCreatedProfessional (professional)
          toggleOkDialog ()
        } catch (error) {
          setSubmitting (false)
          setErrorMessage (getApiErrorMessage (error))
          logApiError (error)
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        isSubmitting,
        setFieldValue,
        handleChange,
        handleBlur,
        handleSubmit
      }) => {
        const searchAddress = async event => {
          const postal_code = event.target.rawValue

          setFieldValue ('postal_code', postal_code)
          
          if (postal_code.length === 8) {
            setLoadingAddress (true)
            try {
              const { data } = await viaCepApi.get (`/ws/${postal_code}/json`)

              setFieldValue ('route', data.logradouro)
              setFieldValue ('district', data.bairro)
              setFieldValue ('city', data.localidade)
              setFieldValue ('state', data.uf)
            } catch (error) {
              logApiError (error)
            } finally {
              setLoadingAddress (false)
            }
          }
        }
  
        return (
          <Form onKeyDown={submitOnEnter(handleSubmit, isSubmitting)} style={{ width: 720 }}>
            <SubTitle>Endereço</SubTitle>
            <AddressContainer>
              {loadingAddress && (
                <LoadingBackdrop>
                  <Loading/>
                </LoadingBackdrop>
              )}
              <Row>
                <FormField>
                  <MaskedInput
                    name='postal_code'
                    placeholder='CEP'
                    options={{
                      blocks: [5,3],
                      delimiters: ['-'],
                      numericOnly: true
                    }}
                    value={values.postal_code}
                    onChange={searchAddress}
                    onBlur={handleBlur ('postal_code')}
                  />
                  {touched.postal_code && errors.postal_code && (
                    <WarningText>{errors.postal_code}</WarningText>
                  )}
                </FormField>
                <FormField stretch={2}>
                  <Input
                    placeholder='Rua/avenida'
                    value={values.route}
                    onChange={handleChange ('route')}
                    onBlur={handleBlur ('route')}
                    disabled={true}
                  />
                </FormField>
                <FormField>
                  <Input
                    placeholder='Número'
                    value={values.number}
                    onChange={handleChange ('number')}
                    onBlur={handleBlur ('number')}
                  />
                  {touched.number && errors.number && (
                    <WarningText>{errors.number}</WarningText>
                  )}
                </FormField>
                <FormField>
                  <Input
                    placeholder='Complemento'
                    value={values.complement}
                    onChange={handleChange ('complement')}
                    onBlur={handleBlur ('complement')}
                  />
                </FormField>
              </Row>
              <Row>
                <FormField stretch={4}>
                  <Input
                    placeholder='Bairro'
                    value={values.district}
                    onChange={handleChange ('district')}
                    onBlur={handleBlur ('district')}
                    disabled={true}
                  />
                </FormField>
                <FormField stretch={3}>
                  <Input
                    placeholder='Cidade'
                    value={values.city}
                    onChange={handleChange ('city')}
                    onBlur={handleBlur ('city')}
                    disabled={true}
                  />
                </FormField>
                <FormField>
                  <Input
                    placeholder='Estado'
                    value={values.state}
                    onBlur={handleBlur ('state')}
                    disabled={true}
                  />
                </FormField>
              </Row>
            </AddressContainer>
            {errorMessage && (
              <ErrorContainer>
                <p>{errorMessage}</p>
              </ErrorContainer>
            )}
            <AlertDialog
              visible={showOkDialog && createdProfessional !== null}
              title='Sucesso'
              message='Profissional cadastrado!'
              confirmText='Ok'
              onConfirm={handleProfessionalAdded}
              onClose={handleProfessionalAdded}
            />
            {professionalData.access_level.value.codename === 'service' ? (
              <MainButton
                leftIcon={<MdNavigateNext size={30}/>}
                aria-label='Próximo'
                onClick={handleSubmit}
                width={60}
              />
            ) : (
              <MainButton
                label='Finalizar cadastro'
                onClick={handleSubmit}
                disabled={isSubmitting}
                loading={isSubmitting}
                width={160}
              />
            )}
          </Form>
        )
      }}
    </Formik>
  )
}
